import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Paper,
  Typography,
} from "@mui/material";

function KnowledgeCard({ title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: "100%",
        backgroundColor: (t) =>
          t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : t.palette.background.paper,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 2.2,
          color: (t) =>
            t.palette.mode === "dark" ? "rgba(255,255,255,0.8)" : t.palette.text.secondary,
          "& li": { mb: 0.75 },
        }}
      >
        {children}
      </Box>
    </Paper>
  );
}

// Knowledge & Assumptions guide for the wind system setup, mirroring the
// equivalent section in PV Setup. Explains the full windpowerlib-based
// modeling chain and what each Modeling Settings option does.
export default function WindKnowledgeSection() {
  return (
    <Accordion
      sx={{
        mt: 2,
        backgroundColor: "transparent",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontSize: "0.85rem",
            color: "text.primary",
          }}
        >
          Knowledge &amp; Assumptions
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Alert
          severity="info"
          sx={{
            mb: 2,
            backgroundColor: (t) =>
              t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : undefined,
          }}
        >
          These notes explain how the wind park is modeled (windpowerlib model chain), what
          each Modeling Setting does, and which assumptions apply when estimating wind
          generation. Estimates run from <b>Weather &amp; Generation → Wind → Generation</b>.
        </Alert>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
            alignItems: "stretch",
          }}
        >
          <KnowledgeCard title="Setting up the wind system — workflow">
            <li>
              <b>1. Turbine:</b> pick a turbine from the catalog (or define a custom one with
              its power curve). The catalog stores the manufacturer power curve, nominal
              power, and — where available — the power coefficient (cp) curve and rotor
              diameter.
            </li>
            <li>
              <b>2. Park layout:</b> set the <b>number of turbines</b>, the <b>hub height</b>{" "}
              (m), and optionally a dedicated <b>park location</b>; otherwise the project
              location is used for weather.
            </li>
            <li>
              <b>3. Farm efficiency (%):</b> flat losses applied to the park output
              (electrical, availability). 100% = no losses. See the wake losses card for how
              it interacts with wake modeling.
            </li>
            <li>
              <b>4. Modeling Settings:</b> defaults are sensible for a typical onshore site —
              you only need to adjust them for special terrain, high-altitude sites, or
              methodological comparisons.
            </li>
          </KnowledgeCard>

          <KnowledgeCard title="Weather inputs">
            <li>
              The model consumes <b>wind speed at 10 m</b> plus an <b>upper-level wind speed</b>{" "}
              (80 m for forecasts, 100 m for historical data) when available, <b>2 m air
              temperature</b>, and <b>surface pressure</b> — all in 15-minute resolution at the
              park location.
            </li>
            <li>
              Hub-height wind speed, temperature, and density are <b>never measured</b> — they
              are computed by the selected models from these inputs.
            </li>
            <li>
              Forecast weather covers roughly <b>-70 days to +11 days</b> around today;
              historical (reanalysis) weather is available up to <b>D-1</b> with a multi-day
              processing lag.
            </li>
            <li>
              The exact series used can be inspected in{" "}
              <b>Weather &amp; Generation → Wind → Weather Data</b>.
            </li>
          </KnowledgeCard>

          <KnowledgeCard title="Wind speed at hub height (wind speed model)">
            <li>
              <b>Hellman (default):</b> power law v<sub>hub</sub> = v ·
              (h<sub>hub</sub>/h<sub>data</sub>)<sup>α</sup>. The <b>Hellman exponent</b> α
              defaults to 0.14 (≈1/7, neutral onshore); use ~0.11 offshore, 0.25+ for rough or
              very stable sites. Leave it empty to derive α = 1/ln(h<sub>hub</sub>/z₀) from the
              roughness length.
            </li>
            <li>
              <b>Logarithmic:</b> log wind profile using the <b>roughness length</b> z₀ and the{" "}
              <b>obstacle height</b> (profile displaced by 0.7 × obstacle height). Preferred
              when terrain roughness is well known.
            </li>
            <li>
              <b>Interpolation / Log interpolation:</b> fit between the 10 m and upper-level
              measurements — closest to "using the data directly", but requires the upper-level
              wind to be available for the interval.
            </li>
            <li>
              <b>Roughness length z₀ (m):</b> ~0.0002 open water, 0.03 open grassland,
              0.1–0.25 farmland with hedges, 0.5–1+ forest/urban.
            </li>
            <li>
              The model extrapolates from the measurement height <b>closest to the hub</b>{" "}
              (usually the 80/100 m level when present).
            </li>
          </KnowledgeCard>

          <KnowledgeCard title="Air density &amp; power output">
            <li>
              <b>Temperature model:</b> hub-height temperature from the 2 m measurement.
              Linear gradient applies the standard lapse rate (−0.0065 K/m). Interpolation
              needs two measured heights, which the platform weather does not provide.
            </li>
            <li>
              <b>Density model:</b> air density at hub height from pressure and temperature —
              ideal gas equation (default) or barometric height formula; the practical
              difference is negligible. Density interpolation needs measured density (not
              available).
            </li>
            <li>
              <b>Density correction (recommended):</b> shifts the power curve to site air
              density instead of the standard 1.225 kg/m³ (IEC 61400-12-1). Matters for hot
              and/or high-altitude sites, where thinner air yields less power.
            </li>
            <li>
              <b>Pressure mode:</b> measured surface pressure, an elevation-based estimate
              when measurements are unavailable ("Measured only" fails instead of
              substituting), or a fixed value you set (101325 Pa = standard atmosphere).
            </li>
            <li>
              <b>Power output model:</b> <i>Power curve</i> (default) reads P(v) from the
              manufacturer curve. <i>Power coefficient curve</i> computes P = ½ρ·(π/4)D²·c
              <sub>p</sub>(v)·v³ — it needs a cp curve and rotor diameter in the catalog, is
              evaluated per turbine, and cannot be combined with wake losses or smoothing.
            </li>
          </KnowledgeCard>

          <KnowledgeCard title="Farm &amp; wake effects">
            <li>
              The park is modeled as a windpowerlib <b>wind farm</b> (aggregated power curve),
              not as turbines × a constant.
            </li>
            <li>
              <b>Wake losses model:</b> <i>dena/knorr wind efficiency curves</i> reduce the
              hub-height wind speed as a function of wind speed ("mean" = average over many
              German farms — dena mean is the default; "extreme" = bounding single-farm
              curves, useful for sensitivity analysis). <i>Constant farm efficiency</i>{" "}
              instead scales the farm power curve by the Farm efficiency %. <i>None</i>{" "}
              disables wake losses.
            </li>
            <li>
              <b>Farm efficiency interaction:</b> with a wind efficiency curve (or None), the
              Farm efficiency % is applied <b>on top</b> as electrical/availability losses.
              With "Constant farm efficiency" it <b>is</b> the wake loss — do not double-count.
            </li>
            <li>
              <b>Power curve smoothing:</b> convolves the power curve with a Gauss
              distribution to represent the spread of wind speeds over the farm area — more
              realistic energy around rated wind speed for multi-turbine parks. The standard
              deviation comes from the <b>turbulence intensity</b> (site-specific; empty =
              derived as TI = 1/ln(h/z₀)) or the empirical <b>Staffell–Pfenninger</b> relation.
            </li>
          </KnowledgeCard>

          <KnowledgeCard title="Assumptions &amp; limitations">
            <li>
              Weather is taken at a <b>single grid point</b>; spatial variation across the park
              is only represented statistically (via smoothing), not per turbine.
            </li>
            <li>
              Wake losses are <b>curve-based</b> (dena/Knorr empirical curves) — the actual
              park layout and wind direction are not modeled.
            </li>
            <li>
              One <b>turbine type per park</b>; all turbines share the same hub height.
            </li>
            <li>
              Icing, curtailment, grid constraints, high-wind hysteresis, and degradation are{" "}
              <b>not modeled</b>. Use the Farm efficiency % to approximate flat losses.
            </li>
            <li>
              With default settings, results reflect the dena-mean wake assumption; set wake
              losses to <b>None</b> to reproduce gross (no-wake) production.
            </li>
            <li>
              Modeling is powered by <b>windpowerlib</b> (oemof); model names in the settings
              match the library's terminology, so its documentation applies directly.
            </li>
          </KnowledgeCard>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
